/**
 * What the wizard says about its film and its save while they run: the footer's gate, the one
 * control left to the trainer beside it, the sentence its live region speaks, and the film zone's
 * first line. Keys, never sentences — the footer and the zone translate.
 *
 * ── A save goes through three phases, and each leaves a different way out ──────────────────
 * `waitingForMedia`: the save was pressed while a cover or a film was still being prepared, and
 * nothing has been written. The trainer may stop the WAIT („Przerwij zapis”): the draft stays, the
 * preparation carries on, and the wizard is theirs again. `writing`: the movement and its cover are
 * being written, and there is no way out — a request half-answered cannot be taken back. Then the
 * film goes up (`videoUploadPercent`), and the trainer may stop the FILM („Przerwij wysyłanie”): the
 * movement is saved by then. Once its bytes are up the confirm runs (`videoConfirming`), and that is
 * not stoppable either, so the control is refused again. Outside a save it is „Anuluj”.
 *
 * ── The film's milestones are spoken, its percentages are not ───────────────────────────────
 * A sentence per percent would talk over the trainer for a minute. A refusal is spoken here only
 * when no save is involved: one that ends a save is the save's failure, and the footer's alert says
 * it — the same sentence twice, from two regions, is the defect this rule removes.
 */

/** Where a running save is. `null` when none runs. */
export type SavePhase = 'waitingForMedia' | 'writing';

/** A sentence as a key, with its values; `reasonKey` is itself translated into `{{reason}}`. */
export interface Phrase {
  readonly key: string;
  readonly values?: Readonly<Record<string, string | number>>;
  readonly reasonKey?: string;
}

/** The footer's secondary control: which of its four meanings it has now. */
export type WizardExit = 'cancel' | 'cancelRefused' | 'stopSave' | 'stopUpload';

export interface SaveProgressInput {
  readonly savePhase: SavePhase | null;
  readonly coverPreparing: boolean;
  /** How far the film's compression has got, in whole percent, or `null`. */
  readonly videoPreparingPercent: number | null;
  /** How far the film's upload has got, in whole percent, or `null`. */
  readonly videoUploadPercent: number | null;
  /** The film's bytes are up and its confirm is in flight; it can no longer be stopped. */
  readonly videoConfirming: boolean;
  /** The refused film's reason (as a key) and its name, or `null`. */
  readonly videoRefusal: { readonly reasonKey: string; readonly fileName: string } | null;
  /** The name of the film chosen and prepared, or `null`. */
  readonly chosenVideoName: string | null;
  /** The save's own failure, as a key, or `null`. */
  readonly saveErrorKey: string | null;
}

export interface SaveProgress {
  /** What the gate says while a save runs, or `null` when none does. */
  readonly busyLine: Phrase | null;
  /** What the live region says about the film now, or `null` for silence. */
  readonly milestone: Phrase | null;
  readonly exit: WizardExit;
}

const FOOTER = 'web:exerciseLibrary.creator.footer';
const MEDIA = 'web:exerciseLibrary.creator.media';

export function saveProgress(input: SaveProgressInput): SaveProgress {
  return { busyLine: busyLine(input), milestone: milestone(input), exit: exit(input) };
}

/** In the order the save does things: a cover, a film, the write, the film going up. */
function busyLine(input: SaveProgressInput): Phrase | null {
  if (input.savePhase === null) return null;
  if (input.videoUploadPercent !== null) {
    return { key: `${FOOTER}.uploadingVideo`, values: { percent: input.videoUploadPercent } };
  }
  if (input.savePhase === 'waitingForMedia') {
    if (input.coverPreparing) return { key: `${FOOTER}.preparingCover` };
    if (input.videoPreparingPercent !== null) {
      return { key: `${FOOTER}.preparingVideo`, values: { percent: input.videoPreparingPercent } };
    }
  }
  return { key: `${FOOTER}.saving` };
}

function milestone(input: SaveProgressInput): Phrase | null {
  // The confirm is still the film going up, so the sentence stands rather than changing back.
  if (input.videoUploadPercent !== null || input.videoConfirming) {
    return { key: `${FOOTER}.announce.videoUploading` };
  }
  if (input.videoPreparingPercent !== null) return { key: `${FOOTER}.announce.videoPreparing` };
  if (input.videoRefusal !== null) {
    // A refusal that ended a save is the save's failure, and the alert says it.
    if (input.savePhase !== null || input.saveErrorKey !== null) return null;
    return {
      key: `${MEDIA}.videoRefused`,
      values: { name: input.videoRefusal.fileName },
      reasonKey: input.videoRefusal.reasonKey,
    };
  }
  return input.chosenVideoName === null
    ? null
    : { key: `${MEDIA}.videoChosen`, values: { name: input.chosenVideoName } };
}

function exit(input: SaveProgressInput): WizardExit {
  if (input.savePhase === null) return 'cancel';
  if (input.videoUploadPercent !== null) return 'stopUpload';
  return input.savePhase === 'waitingForMedia' ? 'stopSave' : 'cancelRefused';
}

/**
 * The film zone's first line: how far the compression has got (the footer's own words, so the two
 * never speak two vocabularies about one wait), the film chosen, the film the movement already has,
 * or the limits.
 */
export function filmZoneStatus(input: {
  readonly preparingPercent: number | null;
  readonly chosenName: string | null;
  readonly hasCurrent: boolean;
}): Phrase {
  if (input.preparingPercent !== null) {
    return { key: `${FOOTER}.preparingVideo`, values: { percent: input.preparingPercent } };
  }
  if (input.chosenName !== null) {
    return { key: `${MEDIA}.videoChosen`, values: { name: input.chosenName } };
  }
  return input.hasCurrent
    ? { key: `${MEDIA}.videoCurrent` }
    : { key: 'exerciseLibrary.createWizard.mediaForm.videoMeta' };
}
